// styled.ts — styled-components del Sidebar (navegación principal del SMM).
// Qué hace: define TODOS los estilos visuales del Sidebar (wrapper, logo, nav,
//   items, rail colapsable, lista de workspaces, footer tools).
// Qué exporta: cada styled-component con `export const`.
// Quién lo usa: components/Sidebar.tsx.
import styled from 'styled-components';
import { NavLink } from 'react-router-dom';

export const SidebarWrapper = styled.aside<{ $isOpen: boolean; $collapsed: boolean }>`
  width: ${({ $collapsed }) => ($collapsed ? '64px' : '260px')};
  height: 100vh;
  flex-shrink: 0;
  background-color: ${({ theme }) => theme.colors.surface};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: width 0.18s ease;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    /* En mobile el sidebar es un overlay deslizante a ancho completo: el rail
       colapsado (desktop) no aplica acá. */
    width: 260px;
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    z-index: 100;
    transform: translateX(${({ $isOpen }) => ($isOpen ? '0' : '-100%')});
    transition: transform 0.2s ease;
    box-shadow: ${({ theme }) => theme.shadows.xl};
  }
`;

export const LogoArea = styled.div`
  background: linear-gradient(160deg, #0f0c29 0%, #1a1a3e 50%, #24243e 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

export const Logo = styled.div`
  display: flex;
  justify-content: center;
  img, video {
    width: 100%;
    height: 168px;
    object-fit: cover;
    display: block;
  }

  .is-collapsed & {
    img, video { height: 52px; }
  }
`;

export const PlanBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 4px ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.sm};
  font-size: 12px;
  font-weight: 600;
  color: #6ee7a8;

  .is-collapsed & {
    padding: 4px 0 ${({ theme }) => theme.spacing.sm};
  }
`;

// Saldo de prueba (trial freemium) — visible solo para quien no tiene plan
// activo. Linkea al paywall para suscribirse.
export const CreditBar = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  border: none;
  background: none;
  cursor: pointer;
  padding: 4px ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.sm};
  font-size: 12px;
  font-weight: 600;
  color: #fbbf24;
  &:hover { color: #fcd34d; }
`;

export const Nav = styled.nav`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.md};
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;

  .is-collapsed & {
    padding: ${({ theme }) => theme.spacing.sm} 8px;
  }
`;

// Botón para colapsar/expandir el rail. Solo desktop (en mobile se oculta).
export const CollapseToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  padding: 6px ${({ theme }) => theme.spacing.sm};
  background: none;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.text};
  }

  .is-collapsed & {
    justify-content: center;
    padding: 6px 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

export const NavSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

export const NavSectionLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.xs};

  .is-collapsed & { display: none; }
`;

export const NavSectionToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  background: none;
  border: none;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.borderRadius.md};

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

export const Chevron = styled.span<{ $open: boolean }>`
  font-size: 10px;
  transition: transform 0.15s ease;
  transform: rotate(${({ $open }) => ($open ? '90deg' : '0deg')});
`;

export const StyledNavLink = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-decoration: none;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  font-size: ${({ theme }) => theme.fontSize.sm};
  transition: all 0.15s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.text};
    text-decoration: none;
  }

  &.active {
    background-color: ${({ theme }) => theme.colors.primary};
    color: white;
  }

  .is-collapsed & {
    justify-content: center;
    gap: 0;
    padding: ${({ theme }) => theme.spacing.sm} 0;
  }
`;

export const NavIcon = styled.span`
  font-size: 14px;
  width: 20px;
  text-align: center;
  flex-shrink: 0;
`;

// Texto de cada item — se oculta en modo colapsado (rail de iconos).
export const Label = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  .is-collapsed & { display: none; }
`;

export const BetaBadge = styled.span`
  font-size: 9px;
  font-weight: 700;
  color: #a78bfa;
  background: rgba(167, 139, 250, 0.12);
  padding: 1px 6px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  margin-left: auto;
  text-transform: uppercase;
  letter-spacing: 0.03em;

  .is-collapsed & { display: none; }
`;

export const WorkspaceNameBanner = styled.div`
  padding: 10px ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.surfaceHover};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const WsNameText = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const WsDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

export const WsIcon = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
`;

export const WorkspaceList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const WsItem = styled.li`
  position: relative;
  &:hover button[data-ws-del] { opacity: 1; pointer-events: auto; }
`;

export const WsDeleteBtn = styled.button`
  position: absolute;
  top: 50%;
  right: 6px;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease, background 0.15s ease, color 0.15s ease;
  z-index: 2;

  &:hover { background: #FEE2E2; color: #DC2626; }

  /* En touch (sin hover) siempre visible. */
  @media (hover: none) { opacity: 1; pointer-events: auto; }
`;

// Item de nav que dispara una acción (abre modal) en vez de rutear. Copia el
// look de StyledNavLink pero es un <button> (sin estado active).
export const NavButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: none;
  background: none;
  text-align: left;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-family: inherit;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.text};
  }
`;

export const CreateWorkspaceButton = styled.button`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: 1px dashed ${({ theme }) => theme.colors.borderHover};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
    background-color: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

export const Spacer = styled.div`
  flex: 1;
`;

export const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  margin: ${({ theme }) => theme.spacing.sm} 0;
`;

// Contenedor del toggle de tema — se oculta en el rail colapsado (expandir
// para cambiar el tema).
export const BottomTool = styled.div`
  padding: 0 4px 6px;
  .is-collapsed & { display: none; }
`;

export const HelpLink = styled.a`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  text-decoration: none;
  transition: all 0.15s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.text};
    text-decoration: none;
  }

  .is-collapsed & {
    justify-content: center;
    gap: 0;
    padding: ${({ theme }) => theme.spacing.sm} 0;
  }
`;

export const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: transparent;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.error};
    color: white;
  }

  .is-collapsed & {
    justify-content: center;
    gap: 0;
    padding: ${({ theme }) => theme.spacing.sm} 0;
  }
`;
